import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../services/supabase';

const PENDING_APPROVALS_EVENT = 'pending-approvals-updated';

export const notifyPendingApprovalsUpdated = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(PENDING_APPROVALS_EVENT));
};

export const usePendingApprovalsCount = (enabled = true) => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const [postsRes, commentsRes] = await Promise.all([
        supabase
          .from('wall_posts')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('wall_comments')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
      ]);

      if (postsRes.error || commentsRes.error) {
        setError('Failed to load pending approvals.');
        return;
      }

      setCount((postsRes.count ?? 0) + (commentsRes.count ?? 0));
    } catch (err) {
      setError('Failed to load pending approvals.');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    refresh();
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const handler = () => {
      refresh();
    };
    window.addEventListener(PENDING_APPROVALS_EVENT, handler);
    return () => window.removeEventListener(PENDING_APPROVALS_EVENT, handler);
  }, [enabled, refresh]);

  return { count, loading, error, refresh };
};
